import { API_ENDPOINTS, httpClientInstance } from "@/api"
import { createFetchGuard, isFresh } from "@/utils/fetch-guard"
import { create } from "zustand"

// ==================== Dashboard Types ====================
// departmentCosts is still mocked (simulated latency) — see the mock data below and
// docs/reports/mocked-vs-integrated-features-audit.md. The real billing summary (get-summary / sync)
// lives in store/billing/cost-reporting.store.ts.
// spendTrend is real: GET /v1/aws-connect/spend-trend/ (aws_connect/views.py SpendTrendView,
// via AWS Cost Explorer using the user's own linked access key). The AWS Invoices panel is
// also real — Dashboard.tsx derives its rows directly from `spendTrend` (each row = one
// calendar month), and its detail modal calls GET /v1/aws-connect/spend-trend/service-breakdown/
// (MonthlyCostByServiceView) for that month's real per-service cost breakdown.
// Per-server utilization (vCPU/memory/CPU/network/uptime) is NOT part of this store — it's computed
// directly in Dashboard.tsx from real data: useManageServerStore (identity + instance_type specs +
// hourly cost) merged with live CloudWatch metrics from GET /v1/aws-connect/instances/{id}/get-metrics/.

/**
 * One month's point on the Spend Trend chart. The AWS Invoices panel also derives its rows
 * directly from this array (Dashboard.tsx) — each point is one calendar month's real AWS cost.
 */
export interface SpendTrendPoint {
  month: string
  cost: number
}

/** Wire shape of SpendTrendView's response (aws_connect/serializers.py SpendTrendSerializer) — `cost` is a DecimalField, serialized as a string like every other money field in this codebase (see CostSummary.mtd_cost). */
interface SpendTrendApiResponse {
  monthly_trend: { month: string; cost: string }[]
  currency: string
}

/** One service's AWS cost within a month's breakdown. */
export interface MonthlyServiceCost {
  service_name: string
  cost: string
}

/** Mirrors aws_connect/serializers.py MonthlyCostByServiceSerializer (GET /v1/aws-connect/spend-trend/service-breakdown/)
 *  — the real per-service AWS cost breakdown for one calendar month, behind the AWS Invoices panel's detail modal. */
export interface AwsInvoiceDetail {
  month: string
  billing_period_start: string
  billing_period_end: string
  currency: string
  total_cost: string
  services: MonthlyServiceCost[]
}

/** Aggregate AWS cost per department for the current billing period — powers the "Spend by Department" pie chart. */
export interface DepartmentCost {
  department_name: string
  cost: number
  status: "active" | "inactive"
}

// ==================== Mock Data ====================
// Still mocked — no backend endpoint aggregates AWS cost per department yet.

const mockDepartmentCosts: DepartmentCost[] = [
  { department_name: "Engineering", cost: 851.35, status: "active" },
  { department_name: "Data & Analytics", cost: 128.9, status: "active" },
  { department_name: "Operations", cost: 214.6, status: "inactive" },
  { department_name: "Sales", cost: 92.15, status: "active" },
  { department_name: "Marketing", cost: 107.1, status: "inactive" },
]

// ==================== Store ====================

interface DashboardStore {
  selectedInvoiceMonth: string | null
  invoiceDetails: Record<string, AwsInvoiceDetail>
  invoiceDetailLoading: boolean
  invoiceDetailError: string | null
  openInvoiceDetail: (month: string) => Promise<void>
  closeInvoiceDetail: () => void

  departmentCosts: DepartmentCost[]
  departmentCostsLoading: boolean
  departmentCostsError: string | null

  spendTrend: SpendTrendPoint[]
  spendTrendLoading: boolean
  spendTrendError: string | null
  /** Epoch ms of the last successful spend-trend fetch; a failed fetch leaves it unchanged. */
  spendTrendFetchedAt: number | null

  /** `includeSpendTrend: false` skips the aws-connect spend-trend request (role-connected customers get a 501 there). */
  fetchDashboardData: (options?: { includeSpendTrend?: boolean }) => Promise<void>
  reset: () => void
}

// Spend trend is billed Cost Explorer traffic: share one pending request and skip refetches while fresh.
const spendTrendGuard = createFetchGuard()

export const useDashboardStore = create<DashboardStore>((set, get) => ({
  selectedInvoiceMonth: null,
  invoiceDetails: {},
  invoiceDetailLoading: false,
  invoiceDetailError: null,

  openInvoiceDetail: async (month) => {
    set({ selectedInvoiceMonth: month, invoiceDetailLoading: true, invoiceDetailError: null })
    await httpClientInstance
      .get<AwsInvoiceDetail>(API_ENDPOINTS.awsConnect.spendTrendServiceBreakdown(month))
      .then((detail) =>
        set((state) => ({
          invoiceDetails: { ...state.invoiceDetails, [month]: detail },
          invoiceDetailLoading: false,
        })),
      )
      .catch((error) =>
        set({
          invoiceDetailError: error?.response?.data?.message || error?.message || "Failed to fetch invoice detail",
          invoiceDetailLoading: false,
        }),
      )
  },

  closeInvoiceDetail: () => set({ selectedInvoiceMonth: null, invoiceDetailError: null }),

  departmentCosts: [],
  departmentCostsLoading: false,
  departmentCostsError: null,

  spendTrend: [],
  spendTrendLoading: false,
  spendTrendError: null,
  spendTrendFetchedAt: null,

  fetchDashboardData: async ({ includeSpendTrend = true } = {}) => {
    // A fresh trend is kept as is; only the request is skipped.
    const spendTrendFresh = includeSpendTrend && isFresh(get().spendTrendFetchedAt)
    const fetchSpendTrend = includeSpendTrend && !spendTrendFresh

    set({
      departmentCostsLoading: true,
      spendTrendLoading: fetchSpendTrend,
      departmentCostsError: null,
      ...(spendTrendFresh ? {} : { spendTrendError: null }),
      ...(includeSpendTrend ? {} : { spendTrend: [] }),
    })

    const spendTrendPromise = fetchSpendTrend
      ? spendTrendGuard.run(async (isCurrent) => {
          try {
            const data = await httpClientInstance.get<SpendTrendApiResponse>(API_ENDPOINTS.awsConnect.spendTrend)
            if (isCurrent()) {
              set({
                spendTrend: data.monthly_trend.map((point) => ({ month: point.month, cost: Number(point.cost) })),
                spendTrendLoading: false,
                spendTrendFetchedAt: Date.now(),
              })
            }
          } catch (error) {
            const e = error as { response?: { data?: { message?: string } }; message?: string }
            if (isCurrent()) {
              set({
                spendTrendError: e?.response?.data?.message || e?.message || "Failed to fetch spend trend",
                spendTrendLoading: false,
              })
            }
          }
        })
      : Promise.resolve()

    // Simulate network latency for the still-mocked department costs
    const mockPromise = new Promise((resolve) => setTimeout(resolve, 350)).then(() =>
      set({ departmentCosts: mockDepartmentCosts, departmentCostsLoading: false }),
    )

    await Promise.all([spendTrendPromise, mockPromise])
  },

  reset: () => {
    spendTrendGuard.reset()
    set({
      selectedInvoiceMonth: null,
      invoiceDetails: {},
      invoiceDetailLoading: false,
      invoiceDetailError: null,
      departmentCosts: [],
      departmentCostsLoading: false,
      departmentCostsError: null,
      spendTrend: [],
      spendTrendLoading: false,
      spendTrendError: null,
      spendTrendFetchedAt: null,
    })
  },
}))
